/**
 * Web Storage that never throws.
 *
 * Reading `window.sessionStorage` (or localStorage) can itself throw — a
 * SecurityError where the browser blocks storage for a framed site (the
 * dashboard's preview frames the storefront; Incognito with third-party
 * cookies blocked), and in some private modes and webviews. An access outside
 * a try in a render or an effect then unmounts the whole app to a blank page.
 * These read as "nothing stored" and drop writes instead (1NOW-10518).
 */
type Kind = "session" | "local";

const store = (kind: Kind): Storage | null => {
  try {
    if (typeof window === "undefined") return null;
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};

export const storageGet = (kind: Kind, key: string): string | null => {
  try {
    return store(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

export const storageSet = (kind: Kind, key: string, value: string): void => {
  try {
    store(kind)?.setItem(key, value);
  } catch {
    /* storage unavailable: nothing to keep */
  }
};

export const storageRemove = (kind: Kind, key: string): void => {
  try {
    store(kind)?.removeItem(key);
  } catch {
    /* nothing to remove */
  }
};
